/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { useState, useMemo } from 'react';
import {
  ShieldCheck,
  Award,
  Users,
  FileCheck2,
  Download,
  Activity,
  CheckCircle2,
  AlertTriangle,
  AlertOctagon,
  Flame,
  HeartPulse,
  Clock,
  TrendingUp,
  Calendar,
  ChevronRight,
  Sparkles,
  Layers,
  ArrowUpRight,
  Shield,
  FileText
} from 'lucide-react';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  Cell,
  PieChart,
  Pie,
  AreaChart,
  Area,
  CartesianGrid
} from 'recharts';
import { CostCenter, SafetyIncident } from '../types.ts';
import { SafetyTalk, DC3Record, CertificateRecord, CompetencyItem } from '../data/securityInitialData.ts';
import { generateHSEQExecutiveDashboardPDF } from '../utils/safetyPdfGenerator.ts';
import { CertificatesExpirationBanner } from './CertificatesExpirationBanner.tsx';
import { evaluateCertificatesAlerts } from '../utils/certificateAlertHelper.ts';

interface HSEQOverviewDashboardProps {
  activeCostCenter: CostCenter | null;
  daysWithoutIncident: number;
  recordDays: number;
  incidents: SafetyIncident[];
  safetyTalks: SafetyTalk[];
  dc3Records: DC3Record[];
  certificates: CertificateRecord[];
  competencies: CompetencyItem[];
  onNavigateSection: (section: 'cruz_seguridad' | 'platicas_5min' | 'formatos_dc3' | 'certificados' | 'matriz_competencias') => void;
}

export const HSEQOverviewDashboard: React.FC<HSEQOverviewDashboardProps> = ({
  activeCostCenter,
  daysWithoutIncident,
  recordDays,
  incidents,
  safetyTalks,
  dc3Records,
  certificates,
  competencies,
  onNavigateSection
}) => {
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const [chartPeriod, setChartPeriod] = useState<'6m' | 'year'>('6m');

  const costCenterTitle = activeCostCenter ? activeCostCenter.name : 'Sede Bajío - Planta Salamanca';
  const costCenterCode = activeCostCenter ? activeCostCenter.code : 'CC-BAJ-01';

  // Metrics calculations
  const totalTalks = safetyTalks.length;
  const totalAttendees = useMemo(() => {
    return safetyTalks.reduce((sum, t) => sum + (Number(t.attendeesCount) || 0), 0);
  }, [safetyTalks]);

  const activeCertificates = useMemo(() => {
    return certificates.filter(c => c.status === 'activo');
  }, [certificates]);

  // Certificates alert evaluation for expiring within 30 days
  const certsAlertEvaluation = useMemo(() => {
    return evaluateCertificatesAlerts(certificates, 30, activeCostCenter ? [activeCostCenter] : undefined);
  }, [certificates, activeCostCenter]);

  const totalDC3 = dc3Records.length;
  const validDC3 = useMemo(() => {
    return dc3Records.filter(d => d.status === 'vigente').length;
  }, [dc3Records]);

  // Incident categories count
  const nearMissCount = incidents.filter(i => i.classification === 'near_miss').length;
  const firstAidCount = incidents.filter(i => i.classification === 'first_aid').length;
  const medTreatCount = incidents.filter(i => i.classification === 'medical_treatment').length;
  const lostTimeCount = incidents.filter(i => i.classification === 'lost_time').length;
  const envCount = incidents.filter(i => i.classification === 'environmental').length;
  const totalIncidents = nearMissCount + firstAidCount + medTreatCount + lostTimeCount + envCount;

  // Pyramid / Siniestrality chart data (Frank Bird Model)
  const pyramidData = useMemo(() => [
    {
      category: 'Accidentes LTI (Incapacitantes)',
      count: lostTimeCount,
      fill: '#ef4444',
      target: 0
    },
    {
      category: 'Accidentes s/ Tiempo Perdido',
      count: medTreatCount,
      fill: '#f97316',
      target: 0
    },
    {
      category: 'Primeros Auxilios en Planta',
      count: firstAidCount,
      fill: '#3b82f6',
      target: 0
    },
    {
      category: 'Casi Incidentes / Cond. Inseguras',
      count: nearMissCount,
      fill: '#f59e0b',
      target: 0
    },
    {
      category: 'Pláticas de Prevención y Hallazgos',
      count: totalTalks,
      fill: '#10b981',
      target: 0
    }
  ], [lostTimeCount, medTreatCount, firstAidCount, nearMissCount, totalTalks]);

  // Real Monthly Trend Data computed from recorded safety talks and incidents
  const trendData = useMemo(() => {
    const monthNames = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];
    const currentMonthIdx = new Date().getMonth();
    
    // Generate last 6 months list
    const months = [];
    for (let i = 5; i >= 0; i--) {
      const d = new Date();
      d.setMonth(currentMonthIdx - i);
      const mIdx = d.getMonth();
      const mYear = d.getFullYear();
      const mLabel = monthNames[mIdx];
      const mKey = `${mYear}-${String(mIdx + 1).padStart(2, '0')}`;

      // Aggregate talks in this month
      const monthTalks = safetyTalks.filter(t => t.date && t.date.startsWith(mKey));
      const talkCount = monthTalks.length;
      const attendees = monthTalks.reduce((sum, t) => sum + (Number(t.attendeesCount) || 0), 0);

      // Aggregate incidents in this month
      const monthIncidents = incidents.filter(inc => inc.date && inc.date.startsWith(mKey));
      const incCount = monthIncidents.length;

      months.push({
        month: mLabel,
        platicas: talkCount,
        incidentes: incCount,
        asistencia: attendees
      });
    }

    return months;
  }, [safetyTalks, incidents]);

  // Certificate Distribution (Pie Chart)
  const certDistributionData = useMemo(() => {
    const counts: Record<string, { count: number; name: string; color: string }> = {
      calidad: { count: 0, name: 'Calidad (ISO 9001 / IATF)', color: '#3b82f6' },
      ambiental: { count: 0, name: 'Gestión Ambiental (ISO 14001)', color: '#10b981' },
      seguridad: { count: 0, name: 'Seguridad y Salud (ISO 45001)', color: '#f59e0b' },
      metrologia: { count: 0, name: 'Metrología y Calibración', color: '#8b5cf6' },
      proteccion_civil: { count: 0, name: 'Protección Civil y Bomberos', color: '#ec4899' }
    };

    certificates.forEach(c => {
      if (counts[c.category]) {
        counts[c.category].count += 1;
      }
    });

    return Object.values(counts).filter(c => c.count > 0);
  }, [certificates]);

  // Attendance dynamically aggregated by Operational Area from Safety Talks
  const areaAttendanceData = useMemo(() => {
    if (safetyTalks.length === 0) {
      return [];
    }

    const areaMap: Record<string, number> = {};
    safetyTalks.forEach(t => {
      const area = t.area?.trim() || 'Área General';
      areaMap[area] = (areaMap[area] || 0) + (Number(t.attendeesCount) || 0);
    });

    return Object.entries(areaMap).map(([name, attendees]) => ({
      name,
      asistentes: attendees
    }));
  }, [safetyTalks]);

  // Handle PDF Generation
  const handleDownloadDashboardPDF = async () => {
    try {
      setIsGeneratingPdf(true);
      await generateHSEQExecutiveDashboardPDF({
        costCenterName: costCenterTitle,
        costCenterCode: costCenterCode,
        daysWithoutIncident,
        recordDays,
        totalIncidentsCount: totalIncidents,
        incidents,
        safetyTalks,
        certificates,
        dc3Records
      });
    } catch (error) {
      console.error('Error al generar PDF de Dashboard HSEQ:', error);
    } finally {
      setIsGeneratingPdf(false);
    }
  };

  return (
    <div className="space-y-6 animate-fadeIn">
      
      {/* Real-time Expiration Alert Banner */}
      {certsAlertEvaluation.hasUrgentAlerts && (
        <CertificatesExpirationBanner
          evaluation={certsAlertEvaluation}
          onNavigateToCertificates={() => onNavigateSection('certificados')}
        />
      )}

      {/* Top Banner with Action Controls */}
      <div className="bg-gradient-to-r from-[#171821] via-[#14151b] to-[#1a1b24] p-5 sm:p-6 rounded-2xl border border-[#2b2d3b] shadow-xl flex flex-col lg:flex-row lg:items-center justify-between gap-5 relative overflow-hidden">
        <div className="space-y-1.5 z-10">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="px-2.5 py-1 rounded-lg bg-emerald-950/90 text-emerald-300 border border-emerald-600/70 text-xs font-mono font-bold flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse"></span>
              SISTEMA INTEGRAL HSEQ
            </span>
            <span className="text-xs font-mono text-slate-400 bg-[#22242f] px-2.5 py-1 rounded-lg border border-[#313444]">
              {costCenterCode} — {costCenterTitle}
            </span>
          </div>
          <h2 className="text-lg sm:text-2xl font-black text-white tracking-tight flex items-center gap-2">
            Tablero Ejecutivo de Seguridad, Salud y Medio Ambiente
          </h2>
          <p className="text-xs sm:text-sm text-slate-400 max-w-2xl">
            Monitoreo en tiempo real de indicadores de siniestralidad, certificaciones auditadas, constancias STPS y programa de pláticas preventivas.
          </p>
        </div>

        {/* Action Buttons */}
        <div className="flex items-center gap-3 z-10 flex-wrap">
          <button
            onClick={handleDownloadDashboardPDF}
            disabled={isGeneratingPdf}
            className="px-4 py-2.5 rounded-xl bg-emerald-600 hover:bg-emerald-500 active:bg-emerald-700 text-white font-bold text-xs flex items-center gap-2 transition-all cursor-pointer shadow-lg shadow-emerald-950/50 disabled:opacity-50"
            title="Descargar Informe Ejecutivo HSEQ en formato PDF formal"
          >
            <Download className="h-4 w-4" />
            <span>{isGeneratingPdf ? 'Generando PDF...' : 'Descargar Informe Ejecutivo (PDF)'}</span>
          </button>
        </div>
      </div>

      {/* KPI METRIC CARDS (No Horas Hombre) */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        
        {/* KPI 1: Días sin Accidentes LTI */}
        <div className="bg-[#14151a] p-4.5 rounded-2xl border border-[#252733] relative overflow-hidden group hover:border-emerald-500/50 transition-all shadow-md">
          <div className="flex items-center justify-between">
            <span className="text-xs font-mono text-slate-400 uppercase font-semibold">Días sin LTI</span>
            <div className="p-2 rounded-xl bg-emerald-950/80 text-emerald-400 border border-emerald-700/60">
              <ShieldCheck className="h-5 w-5" />
            </div>
          </div>
          <div className="mt-2 flex items-baseline gap-2">
            <span className="text-3xl font-black text-emerald-400 font-mono tracking-tight">{daysWithoutIncident}</span>
            <span className="text-xs text-emerald-300 font-bold">Días Consecutivos</span>
          </div>
          <div className="mt-2.5 pt-2.5 border-t border-[#222430] flex items-center justify-between text-[11px] text-slate-400">
            <span>Récord Histórico: <strong className="text-slate-200">{recordDays} días</strong></span>
            <span className="text-emerald-400 font-bold flex items-center gap-0.5">
              100% Meta <TrendingUp className="h-3 w-3" />
            </span>
          </div>
        </div>

        {/* KPI 2: Pláticas de Seguridad (5 Min) */}
        <div 
          onClick={() => onNavigateSection('platicas_5min')}
          className="bg-[#14151a] p-4.5 rounded-2xl border border-[#252733] relative overflow-hidden group hover:border-blue-500/50 transition-all cursor-pointer shadow-md"
        >
          <div className="flex items-center justify-between">
            <span className="text-xs font-mono text-slate-400 uppercase font-semibold">Pláticas de Seguridad</span>
            <div className="p-2 rounded-xl bg-blue-950/80 text-blue-400 border border-blue-700/60 group-hover:scale-105 transition-transform">
              <Users className="h-5 w-5" />
            </div>
          </div>
          <div className="mt-2 flex items-baseline gap-2">
            <span className="text-3xl font-black text-blue-400 font-mono tracking-tight">{totalTalks}</span>
            <span className="text-xs text-slate-400">Impartidas</span>
          </div>
          <div className="mt-2.5 pt-2.5 border-t border-[#222430] flex items-center justify-between text-[11px] text-slate-400">
            <span><strong className="text-slate-200">{totalAttendees}</strong> Asistencias</span>
            <span className="text-blue-400 font-bold">100% Cobertura</span>
          </div>
        </div>

        {/* KPI 3: Certificados HSEQ Activos */}
        <div 
          onClick={() => onNavigateSection('certificados')}
          className="bg-[#14151a] p-4.5 rounded-2xl border border-[#252733] relative overflow-hidden group hover:border-amber-500/50 transition-all cursor-pointer shadow-md"
        >
          <div className="flex items-center justify-between">
            <span className="text-xs font-mono text-slate-400 uppercase font-semibold">Certificados HSEQ</span>
            <div className="p-2 rounded-xl bg-amber-950/80 text-amber-400 border border-amber-700/60 group-hover:scale-105 transition-transform">
              <Award className="h-5 w-5" />
            </div>
          </div>
          <div className="mt-2 flex items-baseline gap-2">
            <span className="text-3xl font-black text-amber-400 font-mono tracking-tight">{activeCertificates.length}</span>
            <span className="text-xs text-slate-400">Activos y Auditados</span>
          </div>
          <div className="mt-2.5 pt-2.5 border-t border-[#222430] flex items-center justify-between text-[11px] text-slate-400">
            <span>ISO 9001 / 14001 / 45001</span>
            <span className="text-amber-400 font-bold">Vigentes</span>
          </div>
        </div>

        {/* KPI 4: Constancias DC-3 STPS */}
        <div 
          onClick={() => onNavigateSection('formatos_dc3')}
          className="bg-[#14151a] p-4.5 rounded-2xl border border-[#252733] relative overflow-hidden group hover:border-purple-500/50 transition-all cursor-pointer shadow-md"
        >
          <div className="flex items-center justify-between">
            <span className="text-xs font-mono text-slate-400 uppercase font-semibold">Constancias DC-3</span>
            <div className="p-2 rounded-xl bg-purple-950/80 text-purple-400 border border-purple-700/60 group-hover:scale-105 transition-transform">
              <FileCheck2 className="h-5 w-5" />
            </div>
          </div>
          <div className="mt-2 flex items-baseline gap-2">
            <span className="text-3xl font-black text-purple-400 font-mono tracking-tight">{totalDC3}</span>
            <span className="text-xs text-slate-400">Acreditaciones</span>
          </div>
          <div className="mt-2.5 pt-2.5 border-t border-[#222430] flex items-center justify-between text-[11px] text-slate-400">
            <span><strong className="text-slate-200">{validDC3}</strong> Vigentes</span>
            <span className="text-purple-400 font-bold">Padrón STPS</span>
          </div>
        </div>

      </div>

      {/* CHARTS SECTION - ROW 1 */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        
        {/* Chart 1: Pirámide de Control de Incidentes & Eventos (7 cols) */}
        <div className="lg:col-span-7 bg-[#14151a] p-5 rounded-2xl border border-[#252733] shadow-md flex flex-col justify-between">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 pb-3 border-b border-[#222430]">
            <div>
              <h3 className="font-bold text-sm sm:text-base text-white flex items-center gap-2">
                <Activity className="h-4 w-4 text-emerald-400" />
                Pirámide de Control de Incidentes y Eventos
              </h3>
              <p className="text-xs text-slate-400">
                Modelo Frank Bird & Clasificación Normativa STPS / ISO 45001
              </p>
            </div>
            <span className="text-[10px] font-mono px-2 py-0.5 rounded bg-emerald-950/80 text-emerald-300 border border-emerald-700/60 w-fit">
              Cero LTI Objetivo
            </span>
          </div>

          <div className="h-64 w-full mt-4">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                layout="vertical"
                data={pyramidData}
                margin={{ top: 5, right: 30, left: 40, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#252733" horizontal={false} />
                <XAxis type="number" stroke="#64748b" tick={{ fontSize: 11 }} />
                <YAxis
                  dataKey="category"
                  type="category"
                  stroke="#94a3b8"
                  tick={{ fontSize: 10 }}
                  width={150}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#181922',
                    border: '1px solid #333644',
                    borderRadius: '8px',
                    color: '#fff',
                    fontSize: '12px'
                  }}
                  formatter={(value: any) => [`${value} Eventos`, 'Total Registrado']}
                />
                <Bar dataKey="count" radius={[0, 6, 6, 0]}>
                  {pyramidData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.fill} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>

          <div className="mt-3 pt-3 border-t border-[#222430] grid grid-cols-2 sm:grid-cols-5 gap-2 text-center text-[10px] text-slate-400">
            <div className="p-1.5 rounded-lg bg-red-950/30 border border-red-900/40">
              <span className="block text-red-400 font-bold font-mono">{lostTimeCount}</span>
              <span>LTI (Incap.)</span>
            </div>
            <div className="p-1.5 rounded-lg bg-orange-950/30 border border-orange-900/40">
              <span className="block text-orange-400 font-bold font-mono">{medTreatCount}</span>
              <span>s/ Tiempo P.</span>
            </div>
            <div className="p-1.5 rounded-lg bg-blue-950/30 border border-blue-900/40">
              <span className="block text-blue-400 font-bold font-mono">{firstAidCount}</span>
              <span>1ros Auxilios</span>
            </div>
            <div className="p-1.5 rounded-lg bg-amber-950/30 border border-amber-900/40">
              <span className="block text-amber-400 font-bold font-mono">{nearMissCount}</span>
              <span>Casi Incid.</span>
            </div>
            <div className="p-1.5 rounded-lg bg-emerald-950/30 border border-emerald-900/40">
              <span className="block text-emerald-400 font-bold font-mono">{totalTalks}</span>
              <span>Pláticas Prev.</span>
            </div>
          </div>
        </div>

        {/* Chart 2: Distribución del Padrón de Certificaciones (5 cols) */}
        <div className="lg:col-span-5 bg-[#14151a] p-5 rounded-2xl border border-[#252733] shadow-md flex flex-col justify-between">
          <div className="flex items-center justify-between pb-3 border-b border-[#222430]">
            <div>
              <h3 className="font-bold text-sm sm:text-base text-white flex items-center gap-2">
                <Award className="h-4 w-4 text-amber-400" />
                Certificaciones HSEQ
              </h3>
              <p className="text-xs text-slate-400">
                Padrón por categoría de acreditación
              </p>
            </div>
            <span className="text-[10px] font-mono px-2 py-0.5 rounded bg-amber-950/80 text-amber-300 border border-amber-700/60">
              {certificates.length} Normas
            </span>
          </div>

          <div className="h-52 w-full mt-2 relative flex items-center justify-center">
            {certificates.length === 0 ? (
              <div className="text-center py-6 px-4 space-y-2">
                <Award className="h-8 w-8 text-slate-600 mx-auto" />
                <p className="text-xs text-slate-400 font-medium">Sin certificaciones registradas</p>
                <button
                  onClick={() => onNavigateSection('certificados')}
                  className="px-3 py-1 bg-amber-950/80 hover:bg-amber-900 border border-amber-700/70 text-amber-300 rounded-lg text-[11px] font-bold cursor-pointer transition-colors"
                >
                  + Registrar Certificación
                </button>
              </div>
            ) : (
              <>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={certDistributionData}
                      cx="50%"
                      cy="50%"
                      innerRadius={45}
                      outerRadius={75}
                      paddingAngle={5}
                      dataKey="count"
                    >
                      {certDistributionData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip
                      contentStyle={{
                        backgroundColor: '#181922',
                        border: '1px solid #333644',
                        borderRadius: '8px',
                        color: '#fff',
                        fontSize: '12px'
                      }}
                      formatter={(value: any, name: any, item: any) => [`${value} Certificación`, item.payload.name]}
                    />
                  </PieChart>
                </ResponsiveContainer>
                
                {/* Center Label */}
                <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                  <span className="text-xl font-black text-white font-mono">{certificates.length}</span>
                  <span className="text-[9px] text-slate-400 uppercase font-mono">Normas</span>
                </div>
              </>
            )}
          </div>

          {/* Legend Details */}
          {certDistributionData.length > 0 && (
            <div className="space-y-1.5 mt-2">
              {certDistributionData.map((item, idx) => (
                <div key={idx} className="flex items-center justify-between text-xs py-1 px-2 rounded-lg bg-[#181920] border border-[#242632]">
                  <div className="flex items-center gap-2">
                    <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: item.color }}></span>
                    <span className="text-slate-300 text-[11px] truncate max-w-[170px]">{item.name}</span>
                  </div>
                  <span className="font-mono font-bold text-white text-[11px]">{item.count}</span>
                </div>
              ))}
            </div>
          )}
        </div>

      </div>

      {/* CHARTS SECTION - ROW 2 */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        
        {/* Chart 3: Tendencia Mensual de Pláticas y Días Seguros (7 cols) */}
        <div className="lg:col-span-7 bg-[#14151a] p-5 rounded-2xl border border-[#252733] shadow-md">
          <div className="flex items-center justify-between pb-3 border-b border-[#222430]">
            <div>
              <h3 className="font-bold text-sm sm:text-base text-white flex items-center gap-2">
                <TrendingUp className="h-4 w-4 text-blue-400" />
                Tendencia de Capacitación y Pláticas Preventivas
              </h3>
              <p className="text-xs text-slate-400">
                Evolución de pláticas de 5 min y personal participante (Últimos 6 meses)
              </p>
            </div>
          </div>

          <div className="h-60 w-full mt-4">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={trendData} margin={{ top: 10, right: 20, left: -10, bottom: 0 }}>
                <defs>
                  <linearGradient id="colorPlaticas" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.4}/>
                    <stop offset="95%" stopColor="#3b82f6" stopOpacity={0}/>
                  </linearGradient>
                  <linearGradient id="colorAsistencia" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#10b981" stopOpacity={0.4}/>
                    <stop offset="95%" stopColor="#10b981" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#252733" />
                <XAxis dataKey="month" stroke="#64748b" tick={{ fontSize: 11 }} />
                <YAxis stroke="#64748b" tick={{ fontSize: 11 }} />
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#181922',
                    border: '1px solid #333644',
                    borderRadius: '8px',
                    color: '#fff',
                    fontSize: '12px'
                  }}
                />
                <Legend wrapperStyle={{ fontSize: '11px', paddingTop: '10px' }} />
                <Area type="monotone" dataKey="asistencia" name="Colaboradores Capacitados" stroke="#10b981" strokeWidth={2} fillOpacity={1} fill="url(#colorAsistencia)" />
                <Area type="monotone" dataKey="platicas" name="Pláticas Realizadas" stroke="#3b82f6" strokeWidth={2} fillOpacity={1} fill="url(#colorPlaticas)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Chart 4: Cobertura de Capacitación por Área Operativa (5 cols) */}
        <div className="lg:col-span-5 bg-[#14151a] p-5 rounded-2xl border border-[#252733] shadow-md flex flex-col justify-between">
          <div className="flex items-center justify-between pb-3 border-b border-[#222430]">
            <div>
              <h3 className="font-bold text-sm sm:text-base text-white flex items-center gap-2">
                <Users className="h-4 w-4 text-purple-400" />
                Asistencia por Área Operativa
              </h3>
              <p className="text-xs text-slate-400">
                Participación en pláticas de inicio de turno
              </p>
            </div>
          </div>

          <div className="h-60 w-full mt-4 flex items-center justify-center">
            {areaAttendanceData.length === 0 ? (
              <div className="text-center py-6 px-4 space-y-2">
                <Users className="h-8 w-8 text-slate-600 mx-auto" />
                <p className="text-xs text-slate-400 font-medium">Sin pláticas registradas por área</p>
                <button
                  onClick={() => onNavigateSection('platicas_5min')}
                  className="px-3 py-1 bg-purple-950/80 hover:bg-purple-900 border border-purple-700/70 text-purple-300 rounded-lg text-[11px] font-bold cursor-pointer transition-colors"
                >
                  + Registrar Plática
                </button>
              </div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={areaAttendanceData} margin={{ top: 10, right: 10, left: -15, bottom: 25 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#252733" />
                  <XAxis 
                    dataKey="name" 
                    stroke="#64748b" 
                    tick={{ fontSize: 9 }}
                    angle={-20}
                    textAnchor="end"
                  />
                  <YAxis stroke="#64748b" tick={{ fontSize: 11 }} />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: '#181922',
                      border: '1px solid #333644',
                      borderRadius: '8px',
                      color: '#fff',
                      fontSize: '12px'
                    }}
                    formatter={(value: any) => [`${value} Asistentes`, 'Participantes']}
                  />
                  <Bar dataKey="asistentes" name="Asistentes" fill="#8b5cf6" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

      </div>

      {/* STRATEGIC PANELS - DETAILED SECTIONS */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        
        {/* Panel A: Certificados y Auditorías Activas */}
        <div className="bg-[#14151a] p-5 rounded-2xl border border-[#252733] shadow-md space-y-4">
          <div className="flex items-center justify-between pb-3 border-b border-[#222430]">
            <div className="flex items-center gap-2.5">
              <div className="p-2 rounded-xl bg-amber-950/80 text-amber-400 border border-amber-700/60">
                <Award className="h-5 w-5" />
              </div>
              <div>
                <h3 className="font-bold text-sm sm:text-base text-white">Certificados y Auditorías HSEQ</h3>
                <p className="text-xs text-slate-400">Padrón de acreditaciones oficiales activas</p>
              </div>
            </div>
            <button
              onClick={() => onNavigateSection('certificados')}
              className="text-xs text-amber-400 hover:text-amber-300 font-bold flex items-center gap-1 cursor-pointer"
            >
              <span>Ver Todos</span>
              <ChevronRight className="h-3.5 w-3.5" />
            </button>
          </div>

          <div className="space-y-2.5">
            {certificates.length === 0 ? (
              <div className="text-center py-6 px-4 rounded-xl bg-[#181922] border border-[#252736] space-y-2">
                <Award className="h-6 w-6 text-slate-600 mx-auto" />
                <p className="text-xs text-slate-400">No hay certificaciones ni auditorías registradas.</p>
                <button
                  onClick={() => onNavigateSection('certificados')}
                  className="px-3 py-1 bg-amber-950/80 text-amber-300 border border-amber-700/70 text-xs font-bold rounded-lg cursor-pointer"
                >
                  Registrar Certificado
                </button>
              </div>
            ) : (
              certificates.slice(0, 3).map(cert => (
                <div key={cert.id} className="p-3 rounded-xl bg-[#181922] border border-[#252736] space-y-1.5">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <h4 className="font-bold text-xs text-white truncate">{cert.name}</h4>
                      <span className="text-[10px] text-slate-400">{cert.issuer}</span>
                    </div>
                    <span className="px-2 py-0.5 rounded-md bg-emerald-950/90 text-emerald-300 border border-emerald-700/60 font-mono text-[9px] font-bold uppercase shrink-0">
                      {cert.status}
                    </span>
                  </div>
                  <div className="flex items-center justify-between text-[10px] text-slate-400 pt-1 border-t border-[#20222e] font-mono">
                    <span>Código: <strong className="text-slate-200">{cert.code}</strong></span>
                    <span>Vigencia: <strong className="text-amber-400">{cert.validUntil}</strong></span>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Panel B: Pláticas de Seguridad Recientes */}
        <div className="bg-[#14151a] p-5 rounded-2xl border border-[#252733] shadow-md space-y-4">
          <div className="flex items-center justify-between pb-3 border-b border-[#222430]">
            <div className="flex items-center gap-2.5">
              <div className="p-2 rounded-xl bg-blue-950/80 text-blue-400 border border-blue-700/60">
                <Users className="h-5 w-5" />
              </div>
              <div>
                <h3 className="font-bold text-sm sm:text-base text-white">Pláticas de Seguridad (5 Min)</h3>
                <p className="text-xs text-slate-400">Últimas sesiones de prevención impartidas</p>
              </div>
            </div>
            <button
              onClick={() => onNavigateSection('platicas_5min')}
              className="text-xs text-blue-400 hover:text-blue-300 font-bold flex items-center gap-1 cursor-pointer"
            >
              <span>Gestionar</span>
              <ChevronRight className="h-3.5 w-3.5" />
            </button>
          </div>

          <div className="space-y-2.5">
            {safetyTalks.length === 0 ? (
              <div className="text-center py-6 px-4 rounded-xl bg-[#181922] border border-[#252736] space-y-2">
                <Users className="h-6 w-6 text-slate-600 mx-auto" />
                <p className="text-xs text-slate-400">No hay pláticas de 5 min registradas.</p>
                <button
                  onClick={() => onNavigateSection('platicas_5min')}
                  className="px-3 py-1 bg-blue-950/80 text-blue-300 border border-blue-700/70 text-xs font-bold rounded-lg cursor-pointer"
                >
                  Registrar Plática
                </button>
              </div>
            ) : (
              safetyTalks.slice(0, 3).map(talk => (
                <div key={talk.id} className="p-3 rounded-xl bg-[#181922] border border-[#252736] space-y-1.5">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-[10px] font-mono font-bold text-[#e52e2e] bg-[#22242e] px-1.5 py-0.2 rounded border border-[#303340]">
                          {talk.id}
                        </span>
                        <span className="text-[10px] text-slate-400 font-mono">{talk.date}</span>
                      </div>
                      <h4 className="font-bold text-xs text-white truncate mt-1">{talk.topic}</h4>
                    </div>
                    <span className="px-2 py-0.5 rounded-md bg-emerald-950/90 text-emerald-300 border border-emerald-700/60 font-mono text-[9px] font-bold shrink-0">
                      {talk.attendeesCount} Asistentes
                    </span>
                  </div>
                  <div className="flex items-center justify-between text-[10px] text-slate-400 pt-1 border-t border-[#20222e]">
                    <span>Facilitador: <strong className="text-slate-200">{talk.facilitator}</strong></span>
                    <span className="font-mono text-slate-400">{talk.area}</span>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

      </div>

      {/* QUICK MODULES NAVIGATION CARDS */}
      <div className="bg-[#14151a] p-5 rounded-2xl border border-[#252733] shadow-md space-y-4">
        <h3 className="font-bold text-sm sm:text-base text-white flex items-center gap-2">
          <Layers className="h-4 w-4 text-emerald-400" />
          Acceso Rápido a Módulos Especializados HSEQ
        </h3>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          
          <button
            onClick={() => onNavigateSection('cruz_seguridad')}
            className="p-4 rounded-xl bg-[#181922] border border-[#262836] hover:border-emerald-500/70 hover:bg-[#1e202c] transition-all text-left group cursor-pointer flex flex-col justify-between h-28 shadow-sm"
          >
            <div className="flex items-center justify-between">
              <div className="p-2 rounded-lg bg-emerald-950/80 text-emerald-400 border border-emerald-700/60 group-hover:scale-105 transition-transform">
                <ShieldCheck className="h-4 w-4" />
              </div>
              <ArrowUpRight className="h-4 w-4 text-slate-500 group-hover:text-white transition-colors" />
            </div>
            <div>
              <h4 className="font-bold text-xs text-white">Cruz de Seguridad</h4>
              <p className="text-[10px] text-slate-400 mt-0.5">Calendario diario y bitácora</p>
            </div>
          </button>

          <button
            onClick={() => onNavigateSection('platicas_5min')}
            className="p-4 rounded-xl bg-[#181922] border border-[#262836] hover:border-blue-500/70 hover:bg-[#1e202c] transition-all text-left group cursor-pointer flex flex-col justify-between h-28 shadow-sm"
          >
            <div className="flex items-center justify-between">
              <div className="p-2 rounded-lg bg-blue-950/80 text-blue-400 border border-blue-700/60 group-hover:scale-105 transition-transform">
                <Users className="h-4 w-4" />
              </div>
              <ArrowUpRight className="h-4 w-4 text-slate-500 group-hover:text-white transition-colors" />
            </div>
            <div>
              <h4 className="font-bold text-xs text-white">Pláticas de 5 Minutos</h4>
              <p className="text-[10px] text-slate-400 mt-0.5">Registro de asistencia y temas</p>
            </div>
          </button>

          <button
            onClick={() => onNavigateSection('formatos_dc3')}
            className="p-4 rounded-xl bg-[#181922] border border-[#262836] hover:border-purple-500/70 hover:bg-[#1e202c] transition-all text-left group cursor-pointer flex flex-col justify-between h-28 shadow-sm"
          >
            <div className="flex items-center justify-between">
              <div className="p-2 rounded-lg bg-purple-950/80 text-purple-400 border border-purple-700/60 group-hover:scale-105 transition-transform">
                <FileCheck2 className="h-4 w-4" />
              </div>
              <ArrowUpRight className="h-4 w-4 text-slate-500 group-hover:text-white transition-colors" />
            </div>
            <div>
              <h4 className="font-bold text-xs text-white">Constancias DC-3 STPS</h4>
              <p className="text-[10px] text-slate-400 mt-0.5">Padrón oficial de habilidades</p>
            </div>
          </button>

          <button
            onClick={() => onNavigateSection('certificados')}
            className="p-4 rounded-xl bg-[#181922] border border-[#262836] hover:border-amber-500/70 hover:bg-[#1e202c] transition-all text-left group cursor-pointer flex flex-col justify-between h-28 shadow-sm"
          >
            <div className="flex items-center justify-between">
              <div className="p-2 rounded-lg bg-amber-950/80 text-amber-400 border border-amber-700/60 group-hover:scale-105 transition-transform">
                <Award className="h-4 w-4" />
              </div>
              <ArrowUpRight className="h-4 w-4 text-slate-500 group-hover:text-white transition-colors" />
            </div>
            <div>
              <h4 className="font-bold text-xs text-white">Certificaciones y Auditorías</h4>
              <p className="text-[10px] text-slate-400 mt-0.5">ISO 9001 / 14001 / 45001</p>
            </div>
          </button>

        </div>
      </div>

    </div>
  );
};
export default HSEQOverviewDashboard;
